import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { 
  MapPin, 
  Search, 
  ArrowLeft, 
  Loader2,
  User,
  Star,
  Calendar,
  Navigation,
  ShieldCheck,
  ChevronDown
} from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const PublicNurseSearch = () => {
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [addressData, setAddressData] = useState(null);
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [searching, setSearching] = useState(false);
  const [nurses, setNurses] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);

  // Today as min date (YYYY-MM-DD)
  const today = new Date().toISOString().split("T")[0];

  // Load all communes for the postal code (a postal code can cover several cities)
  useEffect(() => {
    const fetchCities = async () => {
      if (postalCode.length === 5) {
        setLoadingCity(true);
        try {
          const res = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${postalCode}&fields=nom`);
          const data = res.ok ? await res.json() : [];
          const cities = (data || []).map((c) => c.nom);
          setCityOptions(cities);
          setCity((prev) => (!prev || (cities.length > 0 && !cities.includes(prev)) ? (cities[0] || prev) : prev));
        } catch (e) {
          console.error("Error fetching communes:", e);
        } finally {
          setLoadingCity(false);
        }
      } else {
        setCityOptions([]);
      }
    };
    fetchCities();
  }, [postalCode]);

  const handlePlaceSelect = (place) => {
    setAddressData(place);
    setCity(place.city || "");
    setPostalCode(place.postal_code || "");
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!city && !postalCode && !addressData) {
      toast.error("Veuillez renseigner une adresse ou une ville");
      return;
    }

    setSearching(true);
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      if (city) params.append("city", city);
      if (postalCode) params.append("postal_code", postalCode);
      if (addressData?.lat) params.append("lat", addressData.lat);
      if (addressData?.lng) params.append("lng", addressData.lng);
      if (date) params.append("date", date);
      if (slot) params.append("slot", slot);

      const response = await fetch(`${API}/nurses/search/public?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error("Erreur lors de la recherche");
      }

      const data = await response.json();
      // Backend now handles distance calculation and filtering
      setNurses(data.nurses || []);
    } catch (error) {
      toast.error(error.message);
      setNurses([]);
    } finally {
      setSearching(false);
    }
  };

  const handleMakeRequest = () => {
    // Redirect to register with pre-filled search info (incl. date/slot)
    const params = new URLSearchParams({ type: "patient", redirect: "/care-request" });
    if (city) params.append("city", city);
    if (postalCode) params.append("postal_code", postalCode);
    if (date) params.append("date", date);
    if (slot) params.append("slot", slot);
    navigate(`/register?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#0D1C32]">
      {/* Navigation */}
      <nav className="bg-[#1F2A38] border-b border-[#2A3A4D]">
        <div className="container-ip">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="Infirmiers Privés" className="h-10" />
            </Link>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#94A3B8] hover:text-white">
                  Connexion
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary">
                  S'inscrire
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="container-ip py-8">
        <Link to="/" className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Link>

        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2 text-center">
            Trouver un infirmier près de chez vous
          </h1>
          <p className="text-[#94A3B8] mb-8 text-center">
            Consultez les disponibilités des infirmiers dans votre ville
          </p>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="card-dark p-6 mb-8">
            <div className="space-y-4">
              <div>
                <Label className="form-label">Votre adresse</Label>
                <AddressAutocomplete
                  value={address}
                  onChange={setAddress}
                  onPlaceSelect={handlePlaceSelect}
                  placeholder="Entrez votre adresse pour trouver les infirmiers à proximité..."
                  data-testid="address-autocomplete"
                />
                <p className="text-xs text-[#64748B] mt-1">
                  Nous afficherons uniquement les infirmiers qui interviennent dans votre zone
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Code postal</Label>
                  <div className="relative">
                    <Input
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      className="input-dark"
                      placeholder="75001"
                      maxLength={5}
                      data-testid="postal-code-input"
                    />
                    {loadingCity && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] animate-spin" />
                    )}
                  </div>
                  <p className="text-xs text-[#64748B] mt-1">
                    La ville sera remplie automatiquement
                  </p>
                </div>
                <div>
                  <Label className="form-label">Ville</Label>
                  <div className="relative">
                    <select
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="input-dark w-full appearance-none pr-10"
                      data-testid="city-select"
                    >
                      {!city && <option value="">Renseignez le code postal</option>}
                      {Array.from(new Set([city, ...cityOptions].filter(Boolean))).map((c) => (
                        <option key={c} value={c} className="bg-[#1F2A38] text-white">{c}</option>
                      ))}
                    </select>
                    {loadingCity ? (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] animate-spin" />
                    ) : (
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
                    )}
                  </div>
                </div>
              </div>

              {/* Optional date + slot filter */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="form-label flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date souhaitée <span className="text-[#64748B] font-normal">(optionnel)</span>
                  </Label>
                  <Input
                    type="date"
                    value={date}
                    min={today}
                    onChange={(e) => setDate(e.target.value)}
                    className="input-dark"
                    data-testid="search-date-input"
                  />
                </div>
                <div>
                  <Label className="form-label">
                    Créneau souhaité <span className="text-[#64748B] font-normal">(optionnel)</span>
                  </Label>
                  <select
                    value={slot}
                    onChange={(e) => setSlot(e.target.value)}
                    className="w-full bg-[#1F2A38] border border-[#2A3A4D] text-white rounded-lg px-3 h-10 outline-none focus:border-[#00E073]"
                    data-testid="search-slot-select"
                  >
                    <option value="">Peu importe</option>
                    <option value="matin">Matin (6h-12h)</option>
                    <option value="apres_midi">Après-midi (12h-18h)</option>
                    <option value="soir">Soir (18h-22h)</option>
                    <option value="nuit">Nuit (22h-6h)</option>
                  </select>
                </div>
              </div>
              {date && slot && (
                <p className="text-xs text-[#28CAD4]" data-testid="search-filter-info">
                  Filtrage des infirmiers disponibles le {new Date(date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} ({slot.replace("_", "-")}).
                </p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="btn-primary w-full mt-4"
              disabled={searching}
              data-testid="search-btn"
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recherche...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Rechercher des infirmiers
                </>
              )}
            </Button>
          </form>

          {/* Results */}
          {hasSearched && (
            <div className="space-y-4">
              {nurses === null ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 text-[#00E073] animate-spin" />
                </div>
              ) : nurses.length === 0 ? (
                <div className="card-dark p-8 text-center">
                  <User className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                  <p className="text-[#94A3B8] mb-4">
                    Aucun infirmier trouvé dans cette zone pour le moment.
                  </p>
                  <p className="text-sm text-[#64748B]">
                    Essayez avec une autre ville ou inscrivez-vous pour être notifié.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-[#94A3B8] mb-4">
                    {nurses.length} infirmier(s) disponible(s) dans cette zone
                  </p>
                  
                  {nurses.map((nurse) => (
                    <div 
                      key={nurse.nurse_id}
                      className="card-dark p-6 hover:border-[#00E073]/30 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          <div className="w-16 h-16 rounded-full bg-[#00E073]/20 flex items-center justify-center">
                            {nurse.picture ? (
                              <img src={nurse.picture} alt={nurse.name} className="w-16 h-16 rounded-full object-cover" />
                            ) : (
                              <User className="w-8 h-8 text-[#00E073]" />
                            )}
                          </div>
                          {nurse.is_verified && (
                            <div
                              className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#00E073] rounded-full flex items-center justify-center border-2 border-[#0D1C32]"
                              title="Profil vérifié : photo, paiements actifs et patients satisfaits"
                              data-testid={`verified-badge-${nurse.nurse_id}`}
                            >
                              <ShieldCheck className="w-3.5 h-3.5 text-[#0D1C32]" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-lg font-semibold text-white">{nurse.name}</h3>
                              {nurse.is_verified && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#00E073]/15 text-[#00E073] border border-[#00E073]/30 px-2 py-0.5 rounded-full">
                                  <ShieldCheck className="w-3 h-3" />
                                  Vérifié
                                </span>
                              )}
                            </div>
                            {nurse.distance !== null && nurse.distance !== undefined && (
                              <span className="flex items-center gap-1 text-sm text-[#28CAD4] bg-[#28CAD4]/10 px-2 py-1 rounded-full">
                                <Navigation className="w-3 h-3" />
                                {nurse.distance} km
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-[#94A3B8] mb-2">
                            <MapPin className="w-4 h-4" />
                            {nurse.zones?.slice(0, 3).map(z => 
                              typeof z === 'string' ? z : `${z.city} (${z.radius}km)`
                            ).join(", ")}
                          </div>
                          
                          {nurse.description && (
                            <p className="text-sm text-[#64748B] mb-3 line-clamp-2">
                              {nurse.description}
                            </p>
                          )}
                          
                          {/* Skills removed in MVP (no care-type collection) */}
                          
                          <div className="flex items-center justify-between">
                            <span className="text-[#00E073] font-semibold">
                              À partir de {nurse.base_rate} €
                            </span>
                            
                            {nurse.is_pro && (
                              <span className="flex items-center gap-1 text-yellow-500 text-sm">
                                <Star className="w-4 h-4 fill-yellow-500" />
                                PRO
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* CTA */}
                  <div className="card-dark p-6 text-center border-[#00E073]/30">
                    <Calendar className="w-10 h-10 text-[#00E073] mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Prêt à réserver ?
                    </h3>
                    <p className="text-[#94A3B8] mb-4">
                      Connectez-vous ou créez un compte pour faire une demande d'intervention
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link to="/login">
                        <Button 
                          className="btn-secondary w-full sm:w-auto"
                          data-testid="login-from-search-btn"
                        >
                          Se connecter
                        </Button>
                      </Link>
                      <Button 
                        onClick={handleMakeRequest}
                        className="btn-primary"
                        data-testid="make-request-btn"
                      >
                        Créer un compte et réserver
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PublicNurseSearch;
